import {alpha} from '@mui/material/styles';

// ----------------------------------------------------------------------

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

/**
 * Échelle de couleurs Navy
 * Utilisée pour les interfaces administratives et composants sombres
 * Accessible via theme.palette.navy[50] à theme.palette.navy[900]
 */
const NAVY = {
  50: '#E8EDF5',
  100: '#C5D1E4',
  200: '#9FB3D1',
  300: '#7895BE',
  400: '#5A7EB0',
  500: '#3D67A2',
  600: '#355F94',
  700: '#2B5482',
  800: '#224A71',
  900: '#0F1C2E',
};

const PRIMARY = {
  lighter: '#CCF4FE',
  light: '#68CDF9',
  main: '#078DEE',
  dark: '#0351AB',
  darker: '#012972',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#ffe0b2',
  light: '#ffb74d',
  main: '#ff9800',
  dark: '#f57c00',
  darker: '#e65100',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
  // Fond ambré très clair pour les surfaces d'action (boutons, badges)
  surface: '#FFF5E0',
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

/**
 * Promotion
 *
 * Couleurs utilisées pour représenter les promotions appliquées sur les bons cadeaux.
 * - bonus    : un avantage de valeur offert par l'établissement (+X€ offert)
 * - discount : une remise commerciale (-X€ de réduction)
 *
 * Valeurs volontairement moins saturées que success/error MUI pour rester lisibles
 * sur des fonds clairs et s'accorder avec une charte sobre de reporting.
 */
const PROMOTION = {
  bonus: '#15803d',
  discount: '#b91c1c',
};

/**
 * Purple (Violet)
 * Usage : Features premium, données spéciales, analytics avancés
 */
const PURPLE = {
  lighter: '#F3E8FF',
  light: '#C4B5FD',
  main: '#8B5CF6',
  dark: '#7C3AED',
  darker: '#5B21B6',
  contrastText: '#FFFFFF',
};

/**
 * Pink (Rose)
 * Usage : Métriques wellness, Saint-Valentin, données lifestyle
 */
const PINK = {
  lighter: '#FCE7F3',
  light: '#F9A8D4',
  main: '#EC4899',
  dark: '#DB2777',
  darker: '#9D174D',
  contrastText: '#FFFFFF',
};

/**
 * Teal (Turquoise)
 * Usage : Alternative au success, données environnementales, wellness
 */
const TEAL = {
  lighter: '#CCFBF1',
  light: '#5EEAD4',
  main: '#14B8A6',
  dark: '#0D9488',
  darker: '#115E59',
  contrastText: '#FFFFFF',
};

/**
 * Indigo
 * Usage : Analytics, graphiques, données techniques, intégrations
 */
const INDIGO = {
  lighter: '#E0E7FF',
  light: '#A5B4FC',
  main: '#6366F1',
  dark: '#4F46E5',
  darker: '#3730A3',
  contrastText: '#FFFFFF',
};

/**
 * Slate (Ardoise)
 * Usage : États neutres, données secondaires, éléments désactivés
 * Les clés numériques exposent les nuances Tailwind absentes des crans
 * nommés (50 = fond quasi blanc, 400 = bordures douces).
 */
const SLATE = {
  50: '#F8FAFC',
  400: '#94A3B8',
  lighter: '#F1F5F9',
  light: '#CBD5E1',
  main: '#64748B',
  dark: '#475569',
  darker: '#1E293B',
  contrastText: '#FFFFFF',
};

/**
 * Amber (Ambre)
 * Usage : Alternative au warning, alertes douces, mises en avant
 */
const AMBER = {
  lighter: '#FEF3C7',
  light: '#FCD34D',
  main: '#F59E0B',
  dark: '#D97706',
  darker: '#92400E',
  contrastText: '#212B36',
};

/**
 * Orange
 * Usage : liste d'attente et dates à capacité tendue dans le parcours de
 * réservation client (calendrier, créneaux, légendes).
 * `surface` : fond très clair d'un élément au repos, sous `lighter` au survol.
 */
const ORANGE = {
  surface: '#FFF7ED',
  lighter: '#FFEDD5',
  light: '#FB923C',
  main: '#F97316',
  dark: '#EA580C',
  darker: '#9A3412',
  contrastText: '#FFFFFF',
};

/**
 * Cyan
 * Usage : Données temps réel, streaming, notifications live
 */
const CYAN = {
  lighter: '#CFFAFE',
  light: '#67E8F9',
  main: '#06B6D4',
  dark: '#0891B2',
  darker: '#155E75',
  contrastText: '#FFFFFF',
};

/**
 * Sky (Bleu ciel)
 * Usage : Éléments neutres-positifs, liens, informations secondaires
 */
const SKY = {
  lighter: '#E0F2FE',
  light: '#7DD3FC',
  main: '#0EA5E9',
  dark: '#0284C7',
  darker: '#0369A1',
  contrastText: '#FFFFFF',
};

/**
 * Blue (Bleu royal)
 * Usage : Actions importantes, éléments de confiance, branding fort
 * Les clés numériques exposent les nuances Tailwind absentes des crans
 * nommés (50 = fond d'action très clair, 400 = bordures/accents doux).
 */
const BLUE = {
  50: '#EFF6FF',
  400: '#60A5FA',
  lighter: '#DBEAFE',
  light: '#93C5FD',
  main: '#3B82F6',
  dark: '#2563EB',
  darker: '#1D4ED8',
  contrastText: '#FFFFFF',
};

/**
 * Red (Rouge d'action)
 * Échelle Tailwind « red » : rouge froid des actions destructives et états
 * d'alerte de l'interface, distinct de ERROR (rouge-orangé des états
 * d'erreur). Échelle numérique car les usages couvrent plusieurs nuances
 * (surfaces 50/100, accents 300-400, texte 500-700).
 */
const RED = {
  50: '#FEF2F2',
  100: '#FEE2E2',
  200: '#FECACA',
  300: '#FCA5A5',
  400: '#F87171',
  500: '#EF4444',
  600: '#DC2626',
  700: '#B91C1C',
  800: '#991B1B',
  900: '#7F1D1D',
};

/**
 * Rose (Rose vif)
 * Usage : Promotions, offres spéciales, mise en avant urgente
 */
const ROSE = {
  lighter: '#FFE4E6',
  light: '#FDA4AF',
  main: '#F43F5E',
  dark: '#E11D48',
  darker: '#9F1239',
  contrastText: '#FFFFFF',
};

const STRIPE = {
  lighter: '#FFF4E0',
  light: '#FFCC80',
  main: '#FF9800',
  dark: '#F57C00',
  darker: '#E65100',
  contrastText: GREY[800],
};

const ROLES = {
  admin: {
    lighter: '#FFECD1', // Orange très clair
    light: '#FFB74D', // Orange clair
    main: '#FB8C00', // Orange
    dark: '#EF6C00', // Orange foncé
    darker: '#D84315', // Orange très foncé
    contrastText: '#FFFFFF',
  },
  user: {
    lighter: '#E6F4EA', // Vert très clair
    light: '#81C784', // Vert clair
    main: '#4CAF50', // Vert
    dark: '#388E3C', // Vert foncé
    darker: '#2E7D32', // Vert très foncé
    contrastText: '#FFFFFF',
  },
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  promotion: PROMOTION,
  purple: PURPLE,
  pink: PINK,
  teal: TEAL,
  indigo: INDIGO,
  slate: SLATE,
  amber: AMBER,
  orange: ORANGE,
  cyan: CYAN,
  sky: SKY,
  blue: BLUE,
  red: RED,
  rose: ROSE,
  stripe: STRIPE,
  grey: GREY,
  navy: NAVY,
  roles: ROLES,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(mode) {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: GREY[200],
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}

// Named exports for direct import
export {
  PRIMARY,
  SECONDARY,
  INFO,
  SUCCESS,
  WARNING,
  ERROR,
  PROMOTION,
  STRIPE,
  GREY,
  NAVY,
  PURPLE,
  PINK,
  TEAL,
  INDIGO,
  SLATE,
  AMBER,
  CYAN,
  SKY,
  BLUE,
  RED,
  ROSE,
  ROLES,
};
